import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { formatPrePackLaborDurationMmSs } from "@/pages/prepack/prepackLaborDevHint";

interface TransferPackLaborOperator {
  userId: string;
  userEmail: string | null;
  operatorName: string | null;
  sessionCount: number;
  unitCount: number;
  netSeconds: number;
}

export interface TransferPackLaborSummary {
  transferOrderId: number;
  totalUnitCount: number;
  totalNetSeconds: number;
  operators: TransferPackLaborOperator[];
}

function operatorLabel(operator: TransferPackLaborOperator): string {
  if (operator.operatorName != null && operator.operatorName.trim() !== "") {
    return operator.operatorName.trim();
  }
  if (operator.userEmail != null && operator.userEmail.trim() !== "") {
    return operator.userEmail.trim();
  }
  return operator.userId;
}

/** Compact pack labor summary shown on each transfer card in the workbench list. */
export function TransferPackLaborInline({
  expanded,
  labor,
  isLoading,
  isError,
}: {
  expanded: boolean;
  labor: TransferPackLaborSummary | undefined;
  isLoading: boolean;
  isError: boolean;
}): JSX.Element | null {
  const hasLabor = labor != null && (labor.totalUnitCount > 0 || labor.totalNetSeconds > 0);

  if (isLoading) {
    return <p className="text-xs text-slate-500">Loading pack labor…</p>;
  }

  if (isError) {
    return <p className="text-xs text-rose-700">Could not load pack labor.</p>;
  }

  if (!hasLabor) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-600">
        <span className="font-semibold uppercase tracking-wide text-slate-500">
          Pack labor
          <HoverHintWithGlyph
            hoverHint="Clock-in labor from the pre-pack transfer packing page. One scan is recorded per unit packed into cartons while the operator is clocked in on this transfer."
          />
        </span>
        {": "}
        <span className="font-medium text-slate-900">{String(labor!.totalUnitCount)}</span> packed units
        {" · "}
        <span className="font-medium text-slate-900">
          {formatPrePackLaborDurationMmSs(labor!.totalNetSeconds)}
        </span>{" "}
        attributed time
      </p>
      {expanded ? (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="border-b border-slate-100 px-2 py-1">Operator</th>
                <th className="border-b border-slate-100 px-2 py-1 text-right">Sessions</th>
                <th className="border-b border-slate-100 px-2 py-1 text-right">Units</th>
                <th className="border-b border-slate-100 px-2 py-1 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {labor!.operators.map((operator) => (
                <tr key={operator.userId} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-2 py-1 text-slate-900">{operatorLabel(operator)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {String(operator.sessionCount)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {String(operator.unitCount)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {formatPrePackLaborDurationMmSs(operator.netSeconds)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
